import { useEffect, useState } from 'react';
import { api, type Product } from '../api';

function formatPrice(price: string): string {
  const num = parseFloat(price);
  if (isNaN(num)) return price;
  return new Intl.NumberFormat('ru-RU').format(num);
}

function ProductCard({ product }: { product: Product }) {
  // Изображения находятся на sntea.online
  const imageUrl = product.imageUrl 
    ? (product.imageUrl.startsWith('http') ? product.imageUrl : `https://sntea.online${product.imageUrl}`)
    : 'https://sntea.online/images/products/default.jpg';
  const hasPriceRange = product.priceWholesaleLarge != null;
  
  return (
    <div className="group relative bg-white rounded-xl border border-tea-100 overflow-hidden hover:shadow-md transition-all duration-200">
      <div className="aspect-square bg-tea-50 relative overflow-hidden">
        <img
          src={imageUrl}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          onError={(e) => {
            const target = e.currentTarget;
            target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f5f5f0" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="48" fill="%239ca3af"%3E🍵%3C/text%3E%3C/svg%3E';
          }}
        />
      </div>
      <div className="p-3">
        <h3 className="font-medium text-tea-900 text-xs leading-tight line-clamp-2 min-h-[2rem] mb-2">
          {product.name}
        </h3>
        <div className="flex items-baseline gap-1.5 flex-wrap">
          {hasPriceRange ? (
            <>
              <span className="text-base font-bold text-tea-700">
                от {formatPrice(product.price)} ₽
              </span>
              {product.priceWholesaleLarge && (
                <span className="text-xs text-tea-500">
                  до {formatPrice(product.priceWholesaleLarge)} ₽
                </span>
              )}
            </>
          ) : (
            <span className="text-base font-bold text-tea-700">
              {formatPrice(product.price)} ₽
            </span>
          )}
        </div>
        <div className="mt-1 text-xs text-tea-500">
          за {product.unit}
          {product.minQty > 1 && ` • мин. ${product.minQty}`}
        </div>
      </div>
    </div>
  );
}

export function Catalog() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .products()
      .then((allProducts) => {
        const excludeNames = /сенча|анчан/i;
        const wholesale = allProducts
          .filter((p) => !p.ozonOfferId && !excludeNames.test(p.name));
        setProducts(wholesale.slice(0, 8)); // Превью 6–8 позиций
        setError(null);
      })
      .catch((e) => {
        setError('Не удалось загрузить каталог');
        console.error('[Catalog]', e);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section id="catalog" className="py-16 sm:py-24 px-4 bg-tea-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-tea-900">Каталог товаров</h2>
            <p className="mt-2 text-tea-600 max-w-lg mx-auto">
              Оптовые цены. Минимальный заказ от 5 кг.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-tea-100 animate-pulse">
                <div className="aspect-square bg-tea-100" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-tea-100 rounded w-3/4" />
                  <div className="h-4 bg-tea-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="catalog" className="py-16 sm:py-24 px-4 bg-tea-50/30">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-tea-600">{error}</p>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <section id="catalog" className="py-16 sm:py-24 px-4 bg-tea-50/30">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-tea-900">Каталог товаров</h2>
          <p className="mt-2 text-tea-600 max-w-lg mx-auto">
            Оптовые цены. Минимальный заказ от 5 кг. Полный каталог и актуальные цены — в Telegram.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
        <div className="mt-12 text-center">
          <a
            href={import.meta.env.VITE_TELEGRAM_MINI_APP_URL || 'https://t.me/ShennongTea_bot/app'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-tea-700 text-white font-semibold hover:bg-tea-800 transition-colors shadow-lg shadow-tea-700/20"
          >
            Открыть полный каталог в Telegram
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
